import { LayoutHeader } from '@shinetools/lumen-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import { Contact } from '@features/contacts/types/contact'
import { useSimpleQueryParams } from '@views/receipts/hooks/useSimpleQueryParams'

import { useOrganizationViewUrl } from '../../../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../../../utils/routing/reactRoute'
import { HeaderSupplierDetailActions } from './elements/HeaderSupplierDetailActions'
import { SupplierDrawer } from './elements/SupplierDrawer'

export interface HeaderSupplierDetailProps {
  contact: Contact | null
}

interface SupplierDetailQueryParams {
  editSupplier: string | null
}

const isEditSupplierRequested = (search: string) => new URLSearchParams(search).get('editSupplier') === 'true'

export const HeaderSupplierDetail = ({ contact }: HeaderSupplierDetailProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [, setQueryParams] = useSimpleQueryParams<SupplierDetailQueryParams>({
    defaultQuery: {
      editSupplier: null,
    },
  })
  const { url: suppliersUrl } = useOrganizationViewUrl(reactRoute.organization.suppliers.getMainRoute())
  // The query hook reads window.location, which lags the router in tests and can lag the router
  // right after a replace. The click updates local state immediately. A later router change, such
  // as Back, still follows the URL, except for the navigation this click just triggered.
  const [isDrawerOpen, setIsDrawerOpen] = useState(() => isEditSupplierRequested(location.search))
  const skipNextLocationSyncRef = useRef(false)
  const title = contact?.name ?? t('suppliers.detail.header.unknown_supplier')

  useEffect(() => {
    if (skipNextLocationSyncRef.current) {
      skipNextLocationSyncRef.current = false
    } else {
      setIsDrawerOpen(isEditSupplierRequested(location.search))
    }
  }, [location.search])

  const handleBackButtonClick = () => {
    if (suppliersUrl) {
      navigate(suppliersUrl)
      return
    }

    navigate(-1)
  }

  const handleEditButtonClick = () => {
    skipNextLocationSyncRef.current = true
    setIsDrawerOpen(true)
    setQueryParams({ editSupplier: 'true' })
  }

  const handleDrawerClose = () => {
    skipNextLocationSyncRef.current = true
    setIsDrawerOpen(false)
    setQueryParams({ editSupplier: null })
  }

  return (
    <>
      <LayoutHeader
        actions={<HeaderSupplierDetailActions contact={contact} onEditButtonClick={handleEditButtonClick} />}
        onBack={handleBackButtonClick}
        title={title}
      />
      <SupplierDrawer
        contact={contact}
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        title={t('suppliers.detail.edit_supplier')}
      />
    </>
  )
}
